import React, { useState, useEffect, useContext } from "react";
import "../../CarrierProfile/CarrierProfile.css";
import { useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { Box, Typography, Divider } from "@mui/material";
import PrimaryTabs from "../../TabPanels/PrimaryTabPanel/PrimaryTabs/PrimaryTabs";
import PrimaryTabPanel from "../../TabPanels/PrimaryTabPanel/PrimaryTabPanel";
import TertiaryButton from "../../Buttons/TertiaryButton/TertiaryButton";
import CarrierAutocomplete from "../../Inputs/CarrierAutocomplete/CarrierAutocomplete";
import CarrierMcNumberAutocomplete from "../../Inputs/CarrierMcNumberSearch/CarrierMcNumberAutocomplete";
import CarrierDotNumberAutocomplete from "../../Inputs/CarrierDotNumberAutocomplete/CarrierDotNumberAutocomplete";
import PreferredLanesManual from "./PreferredLanesManual/PreferredLanesManual";
import PostedTrucksManual from "./PostedTrucksManual/PostedTrucksManual";
import PasteCapacity from "./PasteCapacity/PasteCapacity";
import { formatTitleCase } from "../../../utils/formatHelpers";
import { handleAltEnter } from "../../../utils/keyboardShortcutHelpers";
import { CarrierService } from "../../../services/CarrierService";
import { ActiveUserContext, AlertContext } from "../../Dashboard/Dashboard";

export default function CapacityModal() {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const activeUser = useContext(ActiveUserContext);
  let [searchParams] = useSearchParams();
  const [carrier, setCarrier] = useState(null);
  const [action, setAction] = useState("");
  const [type, setType] = useState("");
  const [lane, setLane] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [label, setLabel] = useState("");
  const [tabLabels, setTabLabels] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [submitAllDisabled, setSubmitAllDisabled] = useState(true);
  const [submitAllTriggered, setSubmitAllTriggered] = useState(false);

  const handleClose = () => {
    window.close();
  };

  const handleReset = () => {
    setSubmitAllDisabled(true);
    setSubmitAllTriggered(false);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    handleReset();
  };

  const handleSubmit = () => {
    if (submitAllDisabled) return;

    setSubmitAllDisabled(true);
    setSubmitAllTriggered(true);
  };

  useEffect(() => {
    const carrierName = carrier ? `${formatTitleCase(carrier?.name)}:` : "";
    switch (type) {
      case "preferred-lane":
        document.title = action === "add-capacity" ? `${carrierName} Add Preferred Lane` : `${carrierName} Update Preferred Lane`;
        break;
      case "posted-truck":
        document.title = action === "add-capacity" ? `${carrierName} Add Posted Truck` : `${carrierName} Update Posted Truck`;
        break;
    }
  }, [carrier]);

  useEffect(() => {
    const carrierId = searchParams.get("carrierId");
    if (carrierId) {
      setIsLoading(true);
      CarrierService.GetCarrier(instance, carrierId).then(res => {
        setCarrier(res);
        setIsLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    }

    const actionParam = searchParams.get("action");
    if (actionParam) {
      setAction(actionParam);
    }

    const typeParam = searchParams.get("type");
    if (typeParam) {
      setType(typeParam);

      switch (typeParam) {
        case "preferred-lane":
          setLabel(actionParam === "add-capacity" ? "Add Preferred Lane" : "Update Preferred Lane");
          setTabLabels([{ name: "Manually Enter Preferred Lane" }, { name: "Paste Preferred Lane" }]);
          break;
        case "posted-truck":
          setLabel(actionParam === "add-capacity" ? "Add Posted Truck" : "Update Posted Truck");
          setTabLabels([{ name: "Manually Enter Posted Truck" }, { name: "Paste Posted Truck" }]);
          break;
      }
    }

    if (actionParam === "edit-capacity") {
      const lane = JSON.parse(localStorage.getItem("lane"));
      setLane(lane);
    }
  }, []);

  return (
    <Box onKeyDown={(e) => handleAltEnter(e, (submitAllDisabled || isLoading), handleSubmit)} sx={{ margin: "10px" }}>
      <Box sx={{ marginBottom: "5px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
          <Typography variant="h2">{label}</Typography>
          {tabValue == 1 &&
            <Box>
              <TertiaryButton
                label="CANCEL"
                onClick={handleClose}
              />
              <TertiaryButton
                label="POST ALL"
                onClick={handleSubmit}
                disabled={submitAllDisabled}
                color="primary.main"
              />
            </Box>
          }
        </Box>
        <Divider />
      </Box>
      {action === "add-capacity" && type === "posted-truck" &&
        <Box>
          <Box className="Carrier-modal-input-row">
            <Typography variant="h3" color="primary.60">
              Verify your carrier
            </Typography>
          </Box>
          <Box className="Carrier-modal-input-row">
            <Box>
              <CarrierAutocomplete
                title="Carrier *"
                titleSize="subhead2.fontSize"
                width="200px"
                selectedOption={carrier}
                updateSelectedCarrier={setCarrier}
                error={!carrier}
              />
            </Box>
            <Box sx={{ marginLeft: "20px" }}>
              <CarrierMcNumberAutocomplete
                title="MC # *"
                titleSize="subhead2.fontSize"
                width="140px"
                selectedOption={carrier}
                updateSelectedCarrier={setCarrier}
                error={!carrier}
              />
            </Box>
            <Box sx={{ marginLeft: "20px" }}>
              <CarrierDotNumberAutocomplete
                title="DOT # *"
                titleSize="subhead2.fontSize"
                width="140px"
                selectedOption={carrier}
                updateSelectedCarrier={setCarrier}
                error={!carrier}
              />
            </Box>
          </Box>
        </Box>
      }
      {action === "add-capacity" &&
        <Box sx={{ borderBottom: 1, borderColor: "divider", margin: "0 0 30px 0" }}>
          <PrimaryTabs value={tabValue} handleChange={handleTabChange} tabLabels={tabLabels} typography="subhead1" />
        </Box>
      }
      <PrimaryTabPanel value={tabValue} index={0}>
        {type === "preferred-lane" ?
          <PreferredLanesManual
            action={action}
            userIdMcld={activeUser.userIdMcld}
            activeCarrier={carrier}
            existingLane={lane}
            handleClose={handleClose}
          />
          :
          <PostedTrucksManual
            action={action}
            userIdMcld={activeUser.userIdMcld}
            activeCarrier={carrier}
            existingLane={lane}
            handleClose={handleClose}
          />
        }
      </PrimaryTabPanel>
      <PrimaryTabPanel value={tabValue} index={1}>
        <PasteCapacity
          type={type}
          userIdMcld={activeUser.userIdMcld}
          activeCarrier={carrier}
          handleClose={handleClose}
          setSubmitAllDisabled={setSubmitAllDisabled}
          submitAllTriggered={submitAllTriggered}
        />
      </PrimaryTabPanel>
    </Box>
  );
}

CapacityModal.propTypes = {};